import React, { useState, useEffect } from 'react';
import { ArrowUp } from 'lucide-react';

export const BackToTop: React.FC = () => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    let ticking = false;
    let cachedMaxScroll = 1;

    const updateGeometry = () => {
      const docHeight = document.documentElement?.scrollHeight || document.body?.scrollHeight || 1000;
      cachedMaxScroll = Math.max(1, docHeight - window.innerHeight);
    };

    updateGeometry();
    window.addEventListener('resize', updateGeometry, { passive: true });

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
          const progress = Math.min(100, Math.max(0, (scrollTop / cachedMaxScroll) * 100));
          setScrollProgress(Math.round(progress));

          const shouldBeVisible = scrollTop > 240;
          setIsVisible((prev) => (prev !== shouldBeVisible ? shouldBeVisible : prev));
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('resize', updateGeometry);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // SVG circle geometry
  const size = 44;
  const strokeWidth = 2.2;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (scrollProgress / 100) * circumference;

  return (
    <div
      className={`fixed bottom-6 right-6 z-40 transition-all duration-400 ease-out ${
        isVisible
          ? 'opacity-100 translate-y-0 pointer-events-auto scale-100'
          : 'opacity-0 translate-y-4 pointer-events-none scale-90'
      }`}
    >
      <button
        type="button"
        onClick={scrollToTop}
        aria-label="Back to top"
        title={`Back to top (${Math.round(scrollProgress)}% scrolled)`}
        className="back-to-top-btn group relative flex items-center justify-center w-11 h-11 bg-[#0c1222]/90 hover:bg-[#0c1222] text-[#f5f0e6] hover:text-[#a88345] backdrop-blur-md rounded-full shadow-2xl transition-all duration-300 hover:scale-105 active:scale-95 cursor-pointer border border-[#a88345]/30 overflow-hidden"
        style={{ borderRadius: '9999px' }}
      >
        {/* Circular Progressive Scroll Indicator */}
        <svg
          className="absolute inset-0 w-full h-full -rotate-90 pointer-events-none"
          viewBox={`0 0 ${size} ${size}`}
        >
          {/* Background track circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            className="text-white/10"
          />
          {/* Active progressive scroll stroke in gold */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="#a88345"
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-all duration-150 ease-out"
          />
        </svg>

        {/* Sleek Upward Arrow Icon */}
        <ArrowUp className="w-4 h-4 text-[#a88345] group-hover:text-white transition-all duration-300 group-hover:-translate-y-0.5" />
      </button>
    </div>
  );
};
